import { Student, StandardProgress, Achievement } from './types';
import { mockSchools } from './mockSchools';
import { mockAchievements } from './mockAchievements';
import { mockStandards } from './mockStandards';
import { studentLearningPaths } from './mockStudentLearningPaths';

// Helper function to generate random progress for students
const generateRandomProgress = (): Record<string, StandardProgress> => {
  const progress: Record<string, StandardProgress> = {};
  
  // Add progress for a random subset of standards
  const standardsCount = Math.floor(Math.random() * 8) + 3; // 3-10 standards
  
  for (let i = 0; i < standardsCount; i++) {
    const standardId = `standard-${String(i + 1).padStart(3, '0')}`;
    if (i >= mockStandards.length) break;
    
    const phases = ['NotStarted', 'VideoCompleted', 'PracticeCompleted', 'Completed'] as const;
    const phase = phases[Math.floor(Math.random() * phases.length)];
    
    const videoCompletion = phase === 'NotStarted' 
      ? Math.floor(Math.random() * 30) 
      : phase === 'VideoCompleted' || phase === 'PracticeCompleted' || phase === 'Completed' 
        ? 100 
        : Math.floor(Math.random() * 70) + 30;
        
    const practiceAccuracy = phase === 'NotStarted' || phase === 'VideoCompleted' 
      ? undefined 
      : Math.floor(Math.random() * 40) + 60;
      
    const questionsCompleted = phase === 'NotStarted' || phase === 'VideoCompleted' 
      ? 0 
      : Math.floor(Math.random() * 15) + 5;
      
    const skippedToGame = Math.random() > 0.8;
    
    // Generate a random date within the last 30 days
    const lastActivityDate = new Date();
    lastActivityDate.setDate(lastActivityDate.getDate() - Math.floor(Math.random() * 30));
    
    // For completed standards, set a completed date
    let completedAt;
    if (phase === 'Completed') {
      completedAt = new Date(lastActivityDate);
      completedAt.setDate(completedAt.getDate() - Math.floor(Math.random() * 5));
    }
    
    // Determine completed steps based on learning phase status
    const completedSteps = {
      video: phase === 'VideoCompleted' || phase === 'PracticeCompleted' || phase === 'Completed',
      quiz: phase === 'PracticeCompleted' || phase === 'Completed',
      game: phase === 'Completed'
    };

    progress[standardId] = {
      standardId,
      learningPhaseStatus: phase,
      videoCompletion,
      practiceAccuracy,
      questionsCompleted,
      skippedToGame,
      lastActivity: lastActivityDate.toISOString(),
      completedAt: completedAt?.toISOString(),
      completedSteps
    };
  }
  
  return progress;
};

// Create assigned learning paths array for the first student
const assignedLearningPaths = studentLearningPaths.map(path => path.id);

// Generate a list of 50 students
export const mockStudents: Student[] = Array.from({ length: 50 }, (_, index) => {
  const id = `student-${String(index + 1).padStart(3, '0').toUpperCase()}`;
  const firstName = [
    'Emma', 'Liam', 'Olivia', 'Noah', 'Ava', 'Elijah', 'Sophia', 'Lucas', 'Isabella', 'Mason',
    'Mia', 'Ethan', 'Charlotte', 'Logan', 'Amelia', 'Aiden', 'Harper', 'Grayson', 'Evelyn', 'Jack',
    'Abigail', 'Jackson', 'Emily', 'Wyatt', 'Elizabeth', 'Carter', 'Sofia', 'Sebastian', 'Avery', 'Matthew',
    'Ella', 'Benjamin', 'Scarlett', 'Alexander', 'Grace', 'Michael', 'Chloe', 'William', 'Victoria', 'James',
    'Madison', 'Daniel', 'Lily', 'David', 'Hannah', 'Joseph', 'Zoe', 'Samuel', 'Aria', 'Henry'
  ][index];
  
  const lastName = [
    'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez',
    'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
    'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson',
    'Walker', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores',
    'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell', 'Carter', 'Roberts'
  ][index];
  
  // Distribute students across different schools
  const schoolIndex = index % mockSchools.length;
  
  // Distribute students across different grades
  const grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8'];
  const grade = grades[index % grades.length];
  
  // Assign 2-3 achievements to each student
  const achievements: Achievement[] = [];
  const achievementCount = Math.floor(Math.random() * 2) + 2; // 2-3 achievements
  
  for (let i = 0; i < achievementCount; i++) {
    const achievementIndex = Math.floor(Math.random() * mockAchievements.length);
    if (!achievements.find(a => a.id === mockAchievements[achievementIndex].id)) {
      achievements.push(mockAchievements[achievementIndex]);
    }
  }
  
  // Generate classes for each student (1-3 classes)
  // We'll ensure students are assigned to classes that have learning paths
  // This will make sure students with progress also have learning paths
  const classCount = Math.floor(Math.random() * 3) + 1;
  
  // Use specific classes from mockClasses that have learning paths assigned
  // class-001 to class-005 all have learning paths assigned in mockClasses
  const validClassIds = ['class-001', 'class-002', 'class-003', 'class-004', 'class-005'];
  const classes = Array.from({ length: classCount }, (_, i) => 
    validClassIds[Math.floor(Math.random() * validClassIds.length)]
  );
  
  // Generate progress based on index
  let progress: Record<string, StandardProgress> = {};
  
  // For the first student, create specific progress with completed first standard in first path
  if (index === 0) {
    // Get standards from the first learning path
    const firstPathStandards = studentLearningPaths[0].standards;
    
    // Mark the first standard as completed
    const completedStandardId = firstPathStandards[0].standardId;
    const completedDate = new Date();
    completedDate.setDate(completedDate.getDate() - 3); // Completed 3 days ago
    
    progress[completedStandardId] = {
      standardId: completedStandardId,
      learningPhaseStatus: 'Completed',
      videoCompletion: 100,
      practiceAccuracy: 85,
      questionsCompleted: 10,
      skippedToGame: false,
      lastActivity: completedDate.toISOString(),
      completedAt: completedDate.toISOString(),
      completedSteps: {
        video: true,
        quiz: true,
        game: true
      }
    };
    
    // Add in-progress status for the second standard
    const inProgressStandardId = firstPathStandards[1].standardId;
    const lastActivityDate = new Date();
    lastActivityDate.setHours(lastActivityDate.getHours() - 2); // Last activity 2 hours ago
    
    progress[inProgressStandardId] = {
      standardId: inProgressStandardId,
      learningPhaseStatus: 'VideoCompleted',
      videoCompletion: 100,
      practiceAccuracy: 60,
      questionsCompleted: 3,
      skippedToGame: false,
      lastActivity: lastActivityDate.toISOString(),
      completedAt: undefined,
      completedSteps: {
        video: true,
        quiz: false,
        game: false
      }
    };
    
    // Add not started status for overlapping standards in the second path
    // These standards are already in the first path but we want to show them as not started in the second path context
    const secondPathStandards = studentLearningPaths[1].standards;
    const overlappingStandards = secondPathStandards.filter(s => 
      !progress[s.standardId] && firstPathStandards.some(fps => fps.standardId === s.standardId)
    );
    
    // Add progress entries for standards unique to the second path
    const uniqueSecondPathStandards = secondPathStandards.filter(s => 
      !firstPathStandards.some(fps => fps.standardId === s.standardId)
    );
    
    // Mark all unique standards in the second path as not started
    uniqueSecondPathStandards.forEach(standard => {
      progress[standard.standardId] = {
        standardId: standard.standardId,
        learningPhaseStatus: 'NotStarted',
        videoCompletion: 0,
        questionsCompleted: 0,
        skippedToGame: false,
        lastActivity: new Date().toISOString(),
        completedAt: undefined,
        completedSteps: {
          video: false,
          quiz: false,
          game: false
        }
      };
    });
  } else {
    // For other students, use the random progress generator
    progress = classes.length > 0 ? generateRandomProgress() : {};
  }
  
  // Generate random points between 100 and 5000
  const totalPoints = Math.floor(Math.random() * 4900) + 100;

  return {
    id,
    username: `${firstName.toLowerCase().charAt(0)}${lastName.toLowerCase()}`,
    firstName,
    lastName,
    name: `${firstName} ${lastName}`, // Add the full name property
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@swunmath.edu`,
    role: 'Student',
    avatar: `https://i.pravatar.cc/150?u=${id}`,
    grade,
    classes,
    school: mockSchools[schoolIndex],
    achievements,
    progress,
    totalPoints, // Add the totalPoints property
    assignedLearningPaths: index === 0 ? assignedLearningPaths : undefined
  };
}); 